import React, { useState, useEffect, useContext } from "react";
import Avatar from "@material-ui/core/Avatar";
import { Button } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import { Link } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import LockIcon from "@mui/icons-material/Lock";
import Loading from "../Shared/Loading";
import ButtonTextLoading from "../Shared/ButtonTextLoading";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Signup } from "../../../service/endpoint-manager";

import { useNavigate } from "react-router-dom";

import { useSnackbar } from "notistack";
import { AuthContext } from "../../../context/AuthContext";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#3f51b5",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },

  dateContainer: {
    display: "flex",
    "&>*": {
      flexBasis: "20%",
      flex: "1",
    },
  },

  yearContainer: {
    margin: "0 0 0 0.5rem",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",

    justifyContent: "center",

    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },
  monthContainer: {
    margin: "0 5px 0 0",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },
  dayContainer: {
    margin: "0 5px",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      margin: "0",
      padding: "0.5rem",
    },
  },

  year: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: "0.7rem",
    paddingRight: "0",

    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "scroll",
    },
  },

  month: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: " 0.7rem",
    paddingRight: "0",
    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "scroll",
    },
  },

  day: {
    width: "100%",
    outline: "none",
    borderRadius: "8%",
    padding: "0.7rem",
    paddingRight: "0",
    [theme.breakpoints.down("md")]: {
      maxHeight: "70vh",
      overflow: "hidden",
    },
  },
}));

export default function SignUp() {
  const classes = useStyles();

  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { login, isAuthenticated } = useContext(AuthContext);

  const formik = useFormik({
    initialValues: {
      fullName: "",
      username: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: async function (values) {
      setIsSubmitting(true);
      const result = await Signup(
        values.fullName,
        values.username,
        values.password
      );

      if (result.status === 201) {
        enqueueSnackbar("User Created", {
          variant: "success",
        });
        const response = await login(values.username, values.password);

        if (isAuthenticated) {
          enqueueSnackbar("Login Success", {
            variant: "success",
          });
          navigate("/");
        } else if (response.code == "ERR_BAD_REQUEST") {
          enqueueSnackbar(response.response.data.message, {
            variant: "error",
          });
        } else if (response.code == "ERR_NETWORK") {
          enqueueSnackbar("Network error.", {
            variant: "error",
          });
        }
      } else {
        enqueueSnackbar("Something went wrong. Please try again", {
          variant: "error",
        });
      }
      setIsSubmitting(false);
    },

    // validation schema for formik using yup
    validationSchema: Yup.object({
      // name must be text only and is required
      fullName: Yup.string()
        .matches(/^[a-zA-Z]+(?:[\s.]+[a-zA-Z]+)*$/, "name must be text only")
        .label("Full Name")
        .required("Full name is required"),

      // username must be 6-20 and is required
      username: Yup.string()
        .min(6, "At least 6 characters required")
        .max(20)
        .required("Username is required"),

      // password must be at least 6 characters and is required
      password: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Password is required"),
      // password confirmation must be at least 6 characters and is required
      confirmPassword: Yup.string()
        .min(8, "At least 8 characters required")
        .required("Password is required"),
    }),
  });

  // this useEffect checks to make sure both password and confirmPassword values are supplied
  //and match - sets state accordingly
  useEffect(() => {
    if (
      formik.values.password.length > 0 &&
      formik.values.confirmPassword.length > 0 &&
      formik.values.password !== formik.values.confirmPassword
    ) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  }, [formik.values.password, formik.values.confirmPassword]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        <form
          onSubmit={formik.handleSubmit}
          className={classes.form}
          noValidate
        >
          <Grid container spacing={2}>
            {/* full name */}
            <Grid item xs={12}>
              <TextField
                error={formik.touched.fullName && formik.errors.fullName}
                autoComplete="full-name"
                name="fullName"
                required
                fullWidth
                id="fullName"
                label="Full Name"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fullName}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.fullName && formik.errors.fullName
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.fullName || "Full name is required"}
                </div>
              }
            </Grid>

            {/* username */}
            <Grid item xs={12}>
              <TextField
                error={formik.touched.username && formik.errors.username}
                required
                fullWidth
                id="username"
                label="username"
                name="username"
                autoComplete="username"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.username}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.username && formik.errors.username
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.username || "Username required"}
                </div>
              }
            </Grid>

            {/* password */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.password && formik.errors.password
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.password || "Password required"}
                </div>
              }
            </Grid>

            {/* password confirmation */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                autoComplete="confirm-password"
                variant="outlined"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmPassword}
              />
              {
                <div
                  style={{
                    color: "red",
                    fontSize: "0.6rem",
                    visibility:
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {formik.errors.confirmPassword ||
                    "Password confirmation required"}
                </div>
              }
            </Grid>

            {/* will be shown if passwords dont match as an error message */}
            {
              <div
                style={{
                  color: "red",
                  marginLeft: "2px",
                  fontSize: "0.6rem",
                  visibility: !passwordsMatch ? "visible" : "hidden",
                }}
              >
                Passwords don't match
              </div>
            }
          </Grid>

          {/* submit button */}
          <Button
            type="submit"
            // button will be disabled if form has errors or passwords dont match
            disabled={Object.keys(formik.errors).length > 0 || !passwordsMatch}
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >
            <ButtonTextLoading isSubmitting={isSubmitting} body={"sign up"} />
          </Button>
          <Grid container justifyContent="center">
            <Grid item>
              <Link to="/auth/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>
    </Container>
  );
}
